import React, { useState, useEffect, CSSProperties } from "react";
import { NumberValidationRule } from "types";

interface NumberInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  step?: number;
  rules?: NumberValidationRule;
  className?: string;
  style?: CSSProperties;
  error?: string;
}

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder = "Enter a number",
  min,
  max,
  step = 1,
  rules = {},
  className = "",
  style = {},
  error: externalError,
}) => {
  const [error, setError] = useState<string | null>(null);

  const validate = (val: string): string | null => {
    if (val === "") {
      if (rules.required) {
        return typeof rules.required === "string"
          ? rules.required
          : `${label} is required`;
      }
      return null;
    }

    const numVal = parseFloat(val);
    if (isNaN(numVal)) {
      return `${label} must be a number`;
    }

    if (rules.min && numVal < rules.min.value) {
      return (
        rules.min.message || `${label} must be at least ${rules.min.value}`
      );
    }

    if (rules.max && numVal > rules.max.value) {
      return (
        rules.max.message || `${label} must be no more than ${rules.max.value}`
      );
    }

    if (rules.validate) {
      const result = rules.validate(numVal);
      if (result !== true) {
        return typeof result === "string" ? result : `${label} is invalid`;
      }
    }

    return null;
  };

  useEffect(() => {
    setError(validate(value));
  }, [value]);

  const displayError = externalError || error;

  return (
    <div className={className} style={style}>
      <label
        htmlFor={name}
        className="block mb-2 text-sm font-medium text-[#747171]"
      >
        {label}
      </label>
      {/* <label
        htmlFor={name}
        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
      >
        {label}
      </label> */}
      <input
        type="number"
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        aria-invalid={!!displayError}
        aria-describedby={displayError ? `${name}-error` : undefined}
        className={`text-sm rounded-lg block w-full p-2.5 text-gray-900
          bg-[#F2F2F2] focus:outline-none border-none`.trim()}
      />
      {displayError && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-500"
        >
          {displayError}
        </p>
      )}
    </div>
  );
};

export default NumberInput;

